import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eyebrow } from "../components/ui.jsx";
import { confirmDeletion, validateDeletionToken } from "../lib/deletionApi.js";

/**
 * Token-redemption page reached from the confirmation email. The user lands
 * here with `?token=...` already in the URL; we call /validate to render a
 * summary (and surface the last-admin blocker), then a destructive button
 * calls /confirm to soft-delete the account.
 */
export default function AccountDeleteConfirm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);     // { email, displayName, expiresAt, blockers }
  const [validationError, setValidationError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setValidationError("invalidToken");
        setLoading(false);
        return;
      }
      const { ok, status, data } = await validateDeletionToken(token);
      if (cancelled) return;
      if (ok) {
        setSummary(data);
      } else if (status === 410) {
        setValidationError("alreadyDeleted");
      } else {
        setValidationError("invalidToken");
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function onConfirm() {
    if (confirming) return;
    setConfirmError(null);
    setConfirming(true);
    const { ok, status, data } = await confirmDeletion({ token });
    setConfirming(false);

    if (ok) {
      navigate(`/${lang ?? "en"}/account/delete/done`, { replace: true });
      return;
    }

    if (status === 409 && data?.error === "last_admin") {
      // Refresh the local blocker state so the UI swaps to the blocker view.
      setSummary((s) => s ? {
        ...s,
        blockers: { isLastAdmin: true, organizationName: data.organizationName ?? null }
      } : s);
      setConfirmError("lastAdmin");
    } else if (status === 403) {
      setConfirmError("invalidToken");
    } else if (status === 410) {
      setConfirmError("alreadyDeleted");
    } else if (status === 429) {
      setConfirmError("rateLimited");
    } else {
      setConfirmError("unknown");
    }
  }

  return (
    <main>
      <section className="bg-wash">
        <div className="container" style={{ padding: "56px 24px 32px", maxWidth: 720 }}>
          <Eyebrow>{t("deleteAccountConfirm.eyebrow")}</Eyebrow>
          <h1 className="tt-h tt-h-xl" style={{ marginTop: 24 }}>{t("deleteAccountConfirm.title")}</h1>
        </div>
      </section>

      <section style={{ padding: "32px 0 96px" }}>
        <div className="container" style={{ maxWidth: 640 }}>
          {loading ? (
            <p style={{ fontSize: 17, color: "var(--ink-2)" }}>{t("deleteAccountConfirm.loading")}</p>
          ) : validationError ? (
            <ErrorBlock messageKey={`deleteAccountConfirm.errors.${validationError}`} />
          ) : summary?.blockers?.isLastAdmin ? (
            <BlockerCard
              organization={summary.blockers.organizationName ?? ""}
              titleText={t("deleteAccountConfirm.blockerTitle")}
              bodyText={t("deleteAccountConfirm.blockerBody", { organization: summary.blockers.organizationName ?? "" })}
            />
          ) : summary ? (
            <ConfirmCard
              email={summary.email}
              displayName={summary.displayName}
              expiresAt={summary.expiresAt}
              confirming={confirming}
              confirmError={confirmError}
              onConfirm={onConfirm}
              onCancel={() => navigate(`/${lang ?? "en"}`)}
            />
          ) : null}
        </div>
      </section>
    </main>
  );
}

function ConfirmCard({ email, displayName, expiresAt, confirming, confirmError, onConfirm, onCancel }) {
  const { t } = useTranslation();
  const formattedExpiry = expiresAt
    ? new Date(expiresAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "";
  return (
    <div style={{ background: "var(--bg-2)", border: "1px solid var(--line)", borderRadius: 8, padding: "32px 28px" }}>
      <p
        style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: "var(--ink)" }}
        dangerouslySetInnerHTML={{ __html: t("deleteAccountConfirm.summary", { email, displayName }) }}
      />
      <p style={{ marginTop: 20, fontSize: 13.5, color: "var(--ink-2)" }}>
        {t("deleteAccountConfirm.expires", { expiresAt: formattedExpiry })}
      </p>

      {confirmError ? (
        <div role="alert" style={{ marginTop: 20, padding: "12px 16px", background: "rgba(185, 28, 28, .08)", border: "1px solid rgba(185, 28, 28, .35)", borderRadius: 6, color: "#b91c1c", fontSize: 14, lineHeight: 1.5 }}>
          {t(`deleteAccountConfirm.errors.${confirmError}`)}
        </div>
      ) : null}

      <div style={{ marginTop: 28, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirming}
          className="btn btn-lg"
          style={{ background: "#b91c1c", color: "#fff", flex: "1 1 240px", opacity: confirming ? 0.6 : 1 }}
        >
          {confirming ? t("deleteAccountConfirm.confirming") : t("deleteAccountConfirm.confirm")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={confirming}
          className="btn btn-lg"
          style={{ background: "transparent", color: "var(--ink)", border: "1px solid var(--line)", flex: "0 0 auto" }}
        >
          {t("deleteAccountConfirm.cancel")}
        </button>
      </div>
    </div>
  );
}

function BlockerCard({ organization, titleText, bodyText }) {
  return (
    <div style={{ background: "rgba(185, 28, 28, .06)", border: "1px solid rgba(185, 28, 28, .35)", borderRadius: 8, padding: "32px 28px" }}>
      <h2 style={{ margin: 0, fontFamily: "Satoshi, sans-serif", fontWeight: 600, fontSize: 22, color: "#b91c1c" }}>
        {titleText}
      </h2>
      <p
        style={{ marginTop: 16, fontSize: 16, lineHeight: 1.6, color: "var(--ink)" }}
        dangerouslySetInnerHTML={{ __html: bodyText }}
      />
    </div>
  );
}

function ErrorBlock({ messageKey }) {
  const { t } = useTranslation();
  return (
    <div role="alert" style={{ background: "rgba(185, 28, 28, .06)", border: "1px solid rgba(185, 28, 28, .35)", borderRadius: 8, padding: "24px 28px", color: "var(--ink)", fontSize: 16, lineHeight: 1.6 }}>
      {t(messageKey)}
    </div>
  );
}
